import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Button,
  Space,
  Alert,
  Modal,
  Input,
  Typography,
  message,
  Tag,
  Table,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { WithdrawalReviewPanel } from './WithdrawalReviewPanel';
import {
  fetchWithdrawalRequestDetail,
  approveWithdrawalRequest,
  rejectWithdrawalRequest,
  completeWithdrawalRequest,
  type WithdrawalDetailResponse,
  type WithdrawalAuditLog,
} from '../../services/merchantWithdrawalRequestsService';
import {
  fetchTransactionLogs,
  type TransactionLogsFiltersValues,
  type TransactionLogSummary,
} from '../../services/transactionLogsService';
import { TransactionLogsFilters } from '../transaction-logs/components/TransactionLogsFilters';
import { TransactionLogsTable } from '../transaction-logs/components/TransactionLogsTable';
import { TransactionLogDetailDrawer } from '../transaction-logs/components/TransactionLogDetailDrawer';
import { formatCurrencyVND } from '../../utils/transactionHelpers';
import { spacing } from '../../design-system/tokens';
import { withAdminPathPrefix } from '../../utils/adminPathPrefix';
import { formatVnDateTimeSeconds, vnDayjs } from '../../utils/vnDateTime';

import { WithdrawalDisbursementModalFields } from './WithdrawalDisbursementModalFields';
const { TextArea } = Input;
const { Title, Text } = Typography;
const CARD_STYLE: React.CSSProperties = {
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const TX_PAGE_SIZE = 15;

function approveErrorTitle(code?: string) {
  switch (code) {
    case 'MERCHANT_APPOTAPAY_INSUFFICIENT_BALANCE':
      return 'Số dư AppotaPay merchant không đủ';
    case 'MERCHANT_BALANCE_CHECK_FAILED':
      return 'Không kiểm tra được số dư AppotaPay';
    case 'APPOTAPAY_TRANSFER_FAILED':
      return 'Giải ngân AppotaPay thất bại';
    case 'INSUFFICIENT_BALANCE':
      return 'Số dư nội bộ không đủ';
    case 'INTEGRITY_MISMATCH':
      return 'Dữ liệu lệnh rút bị can thiệp';
    case 'MERCHANT_PAYMENT_NOT_READY':
      return 'Merchant chưa sẵn sàng thanh toán';
    default:
      return 'Không thể duyệt / giải ngân';
  }
}

function resultTag(result?: string) {
  switch (result) {
    case 'SUCCESS':
      return <Tag color="success">Thành công</Tag>;
    case 'FAILED':
      return <Tag color="error">Thất bại</Tag>;
    case 'BLOCKED':
      return <Tag color="warning">Bị chặn</Tag>;
    default:
      return <Tag>{result || '—'}</Tag>;
  }
}

function defaultTxFilters(merchantId: number): TransactionLogsFiltersValues {
  const end = vnDayjs();
  const start = end.subtract(29, 'day');
  return {
    merchantId,
    from: start.format('YYYY-MM-DD'),
    to: end.format('YYYY-MM-DD'),
  };
}

export default function MerchantWithdrawalRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const numericId = parseInt(id || '', 10);

  const [detail, setDetail] = useState<WithdrawalDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [txFilters, setTxFilters] = useState<TransactionLogsFiltersValues>({
    merchantId: undefined,
  });
  const [txRows, setTxRows] = useState<TransactionLogSummary[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txPage, setTxPage] = useState(1);
  const [txPageSize, setTxPageSize] = useState(TX_PAGE_SIZE);
  const [txTotal, setTxTotal] = useState(0);

  const [detailDrawerId, setDetailDrawerId] = useState<number | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [modalNote, setModalNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshingBalance, setRefreshingBalance] = useState(false);

  const loadDetail = useCallback(
    async (refresh = false) => {
      if (!numericId || Number.isNaN(numericId)) return;
      if (refresh) {
        setRefreshingBalance(true);
      } else {
        setLoading(true);
      }
      setLoadError(null);
      try {
        const res = await fetchWithdrawalRequestDetail(numericId, refresh);
        if (res.success && res.data) {
          setDetail(res.data);
          if (!refresh) {
            setTxFilters(defaultTxFilters(res.data.request.restaurant_id));
          }
        } else {
          setLoadError(res.message || 'Không tìm thấy');
        }
      } catch (e: unknown) {
        const ax = e as { response?: { data?: { message?: string } } };
        setLoadError(ax.response?.data?.message || 'Lỗi tải chi tiết');
      } finally {
        setLoading(false);
        setRefreshingBalance(false);
      }
    },
    [numericId]
  );

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const loadTx = useCallback(async () => {
    const mid = txFilters.merchantId;
    if (mid == null) return;
    setTxLoading(true);
    try {
      const res = await fetchTransactionLogs({
        page: txPage,
        limit: txPageSize,
        q: txFilters.q,
        from: txFilters.from,
        to: txFilters.to,
        transactionType: txFilters.transactionType,
        paymentMethod: txFilters.paymentMethod,
        internalStatus: txFilters.internalStatus,
        gatewayStatus: txFilters.gatewayStatus,
        merchantId: mid,
        amountMin: txFilters.amountMin,
        amountMax: txFilters.amountMax,
        hasWebhook: txFilters.hasWebhook,
        failedOnly: txFilters.failedOnly,
        refundedOnly: txFilters.refundedOnly,
      });
      if (res.success && res.data) {
        setTxRows(res.data);
        setTxTotal(res.pagination?.total ?? 0);
      } else {
        setTxRows([]);
        setTxTotal(0);
      }
    } catch {
      message.error('Không tải được transaction logs');
      setTxRows([]);
      setTxTotal(0);
    } finally {
      setTxLoading(false);
    }
  }, [txFilters, txPage, txPageSize]);

  useEffect(() => {
    if (txFilters.merchantId != null) loadTx();
  }, [loadTx, txFilters.merchantId]);

  const req = detail?.request;

  const onRefreshBalances = () => {
    void loadDetail(true);
  };

  const onApprove = async () => {
    setActionLoading(true);
    try {
      const res = await approveWithdrawalRequest(numericId, {
        adminNote: modalNote || null,
      });
      const status = (res as { data?: { status?: string } })?.data?.status;
      if (status === 'PROCESSING') {
        message.success(
          'Đã duyệt và gửi lệnh chi hộ AppotaPay — chờ xác nhận ngân hàng'
        );
      } else if (status === 'COMPLETED') {
        message.success('Đã duyệt và chi hộ AppotaPay thành công');
      } else {
        message.success('Đã duyệt');
      }
      setApproveOpen(false);
      setModalNote('');
      loadDetail();
    } catch (e: unknown) {
      const ax = e as {
        response?: {
          data?: {
            message?: string;
            code?: string;
            details?: {
              merchantBalanceVnd?: number | null;
              publicCode?: string;
              appotapayErrorCode?: number | null;
            };
          };
        };
      };
      const data = ax.response?.data;
      const code = data?.code;
      const details = data?.details;
      Modal.error({
        title: approveErrorTitle(code),
        width: 520,
        content: (
          <div>
            <p style={{ marginBottom: 8 }}>
              {data?.message || 'Lỗi duyệt / chi hộ'}
            </p>
            {details?.merchantBalanceVnd != null ? (
              <p style={{ marginBottom: 4 }}>
                Số dư AppotaPay merchant:{' '}
                <strong>{formatCurrencyVND(details.merchantBalanceVnd)}</strong>
              </p>
            ) : null}
            {req ? (
              <p style={{ marginBottom: 4 }}>
                Số tiền yêu cầu:{' '}
                <strong>{formatCurrencyVND(req.requested_amount_vnd)}</strong>
              </p>
            ) : null}
            {details?.appotapayErrorCode != null ? (
              <p style={{ marginBottom: 0, color: '#888' }}>
                Mã lỗi AppotaPay: {details.appotapayErrorCode}
              </p>
            ) : null}
            {code ? (
              <p style={{ marginBottom: 0, color: '#888' }}>Mã: {code}</p>
            ) : null}
          </div>
        ),
      });
      await loadDetail(true);
    } finally {
      setActionLoading(false);
    }
  };

  const onReject = async () => {
    if (!rejectReason.trim()) {
      message.warning('Nhập lý do từ chối');
      return;
    }
    setActionLoading(true);
    try {
      await rejectWithdrawalRequest(numericId, {
        rejectionReason: rejectReason.trim(),
      });
      message.success('Đã từ chối');
      setRejectOpen(false);
      setRejectReason('');
      loadDetail();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Lỗi từ chối');
    } finally {
      setActionLoading(false);
    }
  };

  const onComplete = async () => {
    setActionLoading(true);
    try {
      await completeWithdrawalRequest(numericId, {
        adminNote: modalNote || null,
      });
      message.success('Đã đánh dấu hoàn tất chuyển khoản');
      setCompleteOpen(false);
      setModalNote('');
      loadDetail();
    } catch (e: unknown) {
      const ax = e as {
        response?: {
          data?: {
            message?: string;
            code?: string;
            details?: {
              merchantBalanceVnd?: number | null;
              appotapayErrorCode?: number | null;
            };
          };
        };
      };
      const data = ax.response?.data;
      const code = data?.code;
      Modal.error({
        title: approveErrorTitle(code),
        content: data?.message || 'Lỗi cập nhật',
      });
      await loadDetail(true);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !req) {
    return (
      <div style={{ padding: spacing.lg }}>
        <Text type="secondary">Đang tải…</Text>
      </div>
    );
  }

  if (loadError || !req || !detail) {
    return (
      <div style={{ padding: spacing.lg }}>
        <Alert type="error" message={loadError || 'Không có dữ liệu'} />
        <Link to={withAdminPathPrefix(pathname, '/withdrawal-requests')}>
          <Button style={{ marginTop: 16 }}>← Danh sách</Button>
        </Link>
      </div>
    );
  }

  return (
    <div style={{ padding: spacing.lg }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Link to={withAdminPathPrefix(pathname, '/withdrawal-requests')}>
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              style={{ paddingLeft: 0 }}
            >
              Danh sách yêu cầu rút tiền
            </Button>
          </Link>
          <Title level={3} style={{ marginTop: 8, marginBottom: 0 }}>
            {req.public_code}
          </Title>
          <Space wrap style={{ marginTop: 8 }}>
            <Tag color="processing">#{req.id}</Tag>
            <Tag>{req.status}</Tag>
            {req.disbursement_provider ? (
              <Tag color="orange">Chi hộ: {req.disbursement_provider}</Tag>
            ) : null}
            {req.transfer_status ? <Tag>CK: {req.transfer_status}</Tag> : null}
          </Space>
        </div>

        <WithdrawalReviewPanel
          detail={detail}
          req={req}
          pathname={pathname}
          refreshing={refreshingBalance}
          onRefresh={onRefreshBalances}
        />

        <Card title="Thông tin yêu cầu" style={CARD_STYLE}>
          <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
            <Descriptions.Item label="Quán">
              {req.restaurant_name || '—'}{' '}
              <Text type="secondary">(ID {req.restaurant_id})</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Người gửi">
              {req.requested_by_email || '—'}
              {req.requested_by_name ? ` · ${req.requested_by_name}` : ''}
              {req.requested_by_phone ? ` · ${req.requested_by_phone}` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Số dư (snapshot lúc gửi)">
              {formatCurrencyVND(req.balance_snapshot_vnd)}
            </Descriptions.Item>
            <Descriptions.Item label="Điểm ví (ưu đãi / không rút được)">
              {formatCurrencyVND(detail.walletLive.currentBalanceVnd)} ·{' '}
              {detail.walletLive.currentBalance} Điểm ·{' '}
              {detail.walletLive.status}
            </Descriptions.Item>
            <Descriptions.Item label="Mã NH (AppotaPay)">
              {req.bank_code || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="AppotaPay ref">
              {req.appotapay_trans_id || req.appotapay_partner_ref_id || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Số tiền yêu cầu rút">
              {formatCurrencyVND(req.requested_amount_vnd)}
            </Descriptions.Item>
            <Descriptions.Item label="Phí (nếu có)">
              {formatCurrencyVND(req.withdrawal_fee_vnd)}
            </Descriptions.Item>
            <Descriptions.Item label="Ước tính sau khi rút">
              {formatCurrencyVND(req.estimated_balance_after_vnd)}
            </Descriptions.Item>
            <Descriptions.Item label="Ngân hàng nhận">
              {[req.bank_name, req.bank_account_number, req.bank_account_name]
                .filter(Boolean)
                .join(' · ') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Tạo lúc">
              {req.created_at ? formatVnDateTimeSeconds(req.created_at) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Duyệt lúc">
              {req.reviewed_at ? formatVnDateTimeSeconds(req.reviewed_at) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Admin xử lý">
              {req.reviewed_by_email || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Hoàn tất CK">
              {req.completed_at
                ? formatVnDateTimeSeconds(req.completed_at)
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Ghi chú admin" span={2}>
              {req.admin_note || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Lý do từ chối" span={2}>
              {req.rejection_reason || '—'}
            </Descriptions.Item>
            {req.transfer_error_message ? (
              <Descriptions.Item label="Lỗi chi hộ" span={2}>
                <Text type="danger">{req.transfer_error_message}</Text>
              </Descriptions.Item>
            ) : null}
          </Descriptions>

          <Space style={{ marginTop: 24 }}>
            {req.status === 'PENDING' ? (
              <>
                <Button
                  type="primary"
                  disabled={detail.reviewHints?.canApprove === false}
                  onClick={() => {
                    setApproveOpen(true);
                  }}
                >
                  Duyệt & chi hộ
                </Button>
                <Button danger onClick={() => setRejectOpen(true)}>
                  Từ chối
                </Button>
              </>
            ) : null}
            {req.status === 'APPROVED' ? (
              <Button
                type="primary"
                onClick={() => {
                  setCompleteOpen(true);
                }}
              >
                Chi hộ qua AppotaPay (yêu cầu cũ)
              </Button>
            ) : null}
            {req.status === 'PROCESSING' ? (
              <Tag color="processing">
                Đang chờ AppotaPay / ngân hàng xác nhận
              </Tag>
            ) : null}
          </Space>
        </Card>

        <Card title="Lịch sử duyệt / giải ngân" style={CARD_STYLE}>
          <Table<WithdrawalAuditLog>
            rowKey="id"
            size="small"
            pagination={false}
            locale={{ emptyText: 'Chưa có lần duyệt nào' }}
            dataSource={detail.auditLogs || []}
            columns={[
              {
                title: 'Thời điểm',
                dataIndex: 'createdAt',
                width: 170,
                render: v => (v ? formatVnDateTimeSeconds(v) : '—'),
              },
              {
                title: 'Thao tác',
                dataIndex: 'action',
                width: 110,
                render: v => v || '—',
              },
              {
                title: 'Kết quả',
                dataIndex: 'result',
                width: 120,
                render: resultTag,
              },
              {
                title: 'Số dư REFER',
                dataIndex: 'merchantAppotapayBalanceVnd',
                width: 140,
                render: v => (v != null ? formatCurrencyVND(Number(v)) : '—'),
              },
              {
                title: 'Số tiền',
                dataIndex: 'requestedAmountVnd',
                width: 130,
                render: v => (v != null ? formatCurrencyVND(Number(v)) : '—'),
              },
              {
                title: 'Chi tiết',
                key: 'detail',
                render: (_, log) => (
                  <Space direction="vertical" size={0}>
                    {log.errorMessage ? (
                      <Text type="danger">{log.errorMessage}</Text>
                    ) : log.appotapayTransId ? (
                      <Text type="secondary">
                        Trans: {log.appotapayTransId}
                      </Text>
                    ) : (
                      <Text type="secondary">—</Text>
                    )}
                    {log.errorCode ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {log.errorCode}
                        {log.ipAddress ? ` · ${log.ipAddress}` : ''}
                      </Text>
                    ) : log.ipAddress ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {log.ipAddress}
                      </Text>
                    ) : null}
                  </Space>
                ),
              },
            ]}
          />
        </Card>

        <Card title="Transaction Logs của quán (đối soát)" style={CARD_STYLE}>
          <TransactionLogsFilters
            lockedMerchantId={req.restaurant_id}
            values={txFilters}
            onChange={next => {
              setTxFilters(next);
              setTxPage(1);
            }}
            onSearch={() => {
              setTxPage(1);
              loadTx();
            }}
            onReset={() => {
              setTxFilters(defaultTxFilters(req.restaurant_id));
              setTxPage(1);
              setTxPageSize(TX_PAGE_SIZE);
            }}
            loading={txLoading}
          />
          <div style={{ marginTop: 16 }}>
            <TransactionLogsTable
              data={txRows}
              loading={txLoading}
              page={txPage}
              pageSize={txPageSize}
              total={txTotal}
              onPageChange={(p, ps) => {
                setTxPage(p);
                if (ps && ps !== txPageSize) setTxPageSize(ps);
              }}
              onViewDetail={record => {
                setDetailDrawerId(record.id);
                setDetailDrawerOpen(true);
              }}
            />
          </div>
        </Card>
      </Space>

      <Modal
        title="Duyệt và chi hộ AppotaPay"
        open={approveOpen}
        onCancel={() => {
          setApproveOpen(false);
          setModalNote('');
        }}
        onOk={onApprove}
        confirmLoading={actionLoading}
        okText="Duyệt & chi hộ"
        width={520}
        destroyOnClose
      >
        {detail.reviewHints?.warnings?.length ? (
          <Alert
            type="warning"
            showIcon
            message="Lưu ý trước khi duyệt"
            description={
              <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                {detail.reviewHints.warnings.map(w => (
                  <li key={w.code}>{w.message}</li>
                ))}
              </ul>
            }
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <WithdrawalDisbursementModalFields
          note={modalNote}
          onNoteChange={setModalNote}
        />
      </Modal>

      <Modal
        title="Từ chối yêu cầu"
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        onOk={onReject}
        confirmLoading={actionLoading}
        okText="Từ chối"
        okButtonProps={{ danger: true }}
      >
        <TextArea
          rows={4}
          placeholder="Lý do từ chối (bắt buộc)"
          value={rejectReason}
          onChange={e => setRejectReason(e.target.value)}
        />
      </Modal>

      <Modal
        title="Chi hộ qua AppotaPay"
        open={completeOpen}
        onCancel={() => {
          setCompleteOpen(false);
          setModalNote('');
        }}
        onOk={onComplete}
        confirmLoading={actionLoading}
        okText="Gửi lệnh chi hộ"
        width={520}
        destroyOnClose
      >
        <p style={{ marginBottom: 16 }}>
          Hệ thống sẽ gọi API chi hộ AppotaPay tới tài khoản merchant đã duyệt.
          Nếu ngân hàng xử lý async, trạng thái sẽ chuyển sang &quot;Đang chi
          hộ&quot; cho đến khi nhận IPN.
        </p>
        <WithdrawalDisbursementModalFields
          note={modalNote}
          onNoteChange={setModalNote}
        />
      </Modal>

      <TransactionLogDetailDrawer
        id={detailDrawerId}
        open={detailDrawerOpen}
        onClose={() => {
          setDetailDrawerOpen(false);
          setDetailDrawerId(null);
        }}
      />
    </div>
  );
}
